// The document-template registry + field validation.
//
// `validateTemplateFields` is the gate between an external model's free-form
// output and an ontology Action input: it rejects unknown keys, missing
// required blanks, wrong types, and over-long values BEFORE the Action runs.
// The controller must never widen an Action to accept whatever the model sent
// (CLAUDE.md → "Soft rules at the mapping seam").

import { z } from 'zod';
import type { DocumentFieldSpec, DocumentTemplateSpec } from './types';
import { CALL_NOTES_TEMPLATE } from './call-notes';

/** Every template exposed over the wire, keyed by slug. */
export const DOCUMENT_TEMPLATES: ReadonlyArray<DocumentTemplateSpec> = [CALL_NOTES_TEMPLATE];

export const DOCUMENT_TEMPLATE_SLUGS = DOCUMENT_TEMPLATES.map((t) => t.slug);

export function getDocumentTemplate(slug: string): DocumentTemplateSpec | undefined {
    return DOCUMENT_TEMPLATES.find((t) => t.slug === slug);
}

// ---------------------------------------------------------------------------
// Field validation
// ---------------------------------------------------------------------------

/** An ISO date or datetime string the renderer can parse. */
const isoDate = z
    .string()
    .min(1)
    .refine((v) => !Number.isNaN(Date.parse(v)), { message: 'must be an ISO date string' });

/**
 * Ceilings applied to every field regardless of what its spec declares, so an
 * unbounded blank cannot become an unbounded database row. A spec's own
 * `maxLength` may be tighter, never looser.
 */
export const FIELD_LENGTH_CEILING = 2_000;
export const LIST_ITEM_CEILING = 100;
export const LIST_ITEM_LENGTH_CEILING = 300;

function schemaForField(field: DocumentFieldSpec): z.ZodTypeAny {
    switch (field.type) {
        case 'text':
        case 'paragraph': {
            // A closed value set is a validation rule, not a hint (see
            // DocumentFieldSpec.enum).
            if (field.enum && field.enum.length > 0) {
                return z.enum([...field.enum] as [string, ...string[]], {
                    message: `must be one of: ${field.enum.join(', ')}`,
                });
            }
            const limit = Math.min(field.maxLength ?? FIELD_LENGTH_CEILING, FIELD_LENGTH_CEILING);
            return z
                .string()
                .min(1, { message: 'must not be empty' })
                .max(limit, { message: `must be at most ${limit} characters` });
        }
        case 'list': {
            const items = Math.min(field.maxLength ?? LIST_ITEM_CEILING, LIST_ITEM_CEILING);
            return z
                .array(
                    z
                        .string()
                        .min(1, { message: 'list items must not be empty' })
                        .max(LIST_ITEM_LENGTH_CEILING, {
                            message: `each item must be at most ${LIST_ITEM_LENGTH_CEILING} characters`,
                        }),
                )
                .min(1, { message: 'must contain at least one item' })
                .max(items, { message: `must contain at most ${items} items` });
        }
        // zod's own type-mismatch message ("Expected number, received string")
        // is already actionable, and its custom-message option key is snake_case
        // — which the #278 port guard rejects in this package.
        case 'currency':
            return z.number();
        case 'percentage':
            return z
                .number()
                .min(0, { message: 'must be at least 0' })
                .max(100, { message: 'must be at most 100' });
        case 'date':
            return isoDate;
    }
}

/**
 * Build a strict zod object for a template's fields. Unknown keys are rejected
 * so a filling model cannot smuggle extra data past the seam; optional blanks
 * (`required: false`) may be omitted entirely.
 */
export function buildTemplateFieldsSchema(spec: DocumentTemplateSpec): z.ZodTypeAny {
    const shape: Record<string, z.ZodTypeAny> = {};
    for (const field of spec.fields) {
        const base = schemaForField(field);
        shape[field.key] = field.required === false ? base.optional() : base;
    }
    return z.object(shape).strict();
}

export interface TemplateFieldError {
    /** The offending field key, or '(root)' for a whole-object problem. */
    key: string;
    message: string;
}

export type TemplateValidationResult =
    | { ok: true; fields: Record<string, unknown> }
    | { ok: false; errors: ReadonlyArray<TemplateFieldError> };

/**
 * Validate a caller-supplied `fields` map against a template's declared blanks.
 * Returns the parsed values on success, or a per-field error list the MCP tool
 * can hand straight back to the model so it can correct itself.
 */
export function validateTemplateFields(
    spec: DocumentTemplateSpec,
    fields: unknown,
): TemplateValidationResult {
    const parsed = buildTemplateFieldsSchema(spec).safeParse(fields);
    if (parsed.success) {
        return { ok: true, fields: parsed.data as Record<string, unknown> };
    }
    const errors = parsed.error.issues.map((issue) => ({
        key: issue.path.length > 0 ? issue.path.join('.') : '(root)',
        message: issue.message,
    }));
    return { ok: false, errors };
}
